import React, { useEffect, useMemo, useState } from "react";
import api from "../lib/api";
import { PageHeader } from "./DashboardLayout";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Checkbox } from "../components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Loader2, Save, Image as ImageIcon, AlertCircle, RefreshCcw, ImageOff, FolderTree } from "lucide-react";
import { toast } from "sonner";

function filenameOf(url) {
  try {
    const u = new URL(url);
    const name = u.pathname.split("/").filter(Boolean).pop() || u.hostname;
    return name.length > 60 ? name.slice(0, 57) + "…" : name;
  } catch {
    return url.split("/").pop() || url;
  }
}

// Reduce a full URL to just its path (e.g. /gallery/spring-2026) — the most
// useful "structural context" anchor for the operator.
function pagePathOf(url) {
  if (!url) return "—";
  try {
    const u = new URL(url);
    const p = (u.pathname || "/").replace(/\/+$/, "") || "/";
    return p.length > 48 ? p.slice(0, 45) + "…" : p;
  } catch {
    return url.length > 48 ? url.slice(0, 45) + "…" : url;
  }
}

export default function ImageManager() {
  const [deployments, setDeployments] = useState([]);
  const [auditDomains, setAuditDomains] = useState([]); // domains with images but no deployment
  const [domain, setDomain] = useState("");
  const [images, setImages] = useState([]);
  const [drafts, setDrafts] = useState({}); // {imageId: {alt_text_override, is_decorative}}
  const [loadingDomains, setLoadingDomains] = useState(true);
  const [loadingImages, setLoadingImages] = useState(false);
  const [saving, setSaving] = useState(false);

  // Load both deployed clients + audited-only domains (server-side crawler
  // populates managed_images even before a Silent Shield deployment exists).
  useEffect(() => {
    (async () => {
      setLoadingDomains(true);
      try {
        const [depR, domR] = await Promise.all([
          api.get("/deployments"),
          api.get("/v1/images/domains"),
        ]);
        setDeployments(depR.data);
        const deployed = new Set(depR.data.map((d) => d.client_domain));
        setAuditDomains((domR.data || []).filter((d) => !deployed.has(d.client_domain)));
        if (!domain) {
          if (depR.data.length > 0) {
            setDomain(depR.data[0].client_domain);
          } else if (domR.data?.length > 0) {
            setDomain(domR.data[0].client_domain);
          }
        }
      } catch {
        toast.error("Could not load client deployments");
      } finally {
        setLoadingDomains(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Load images for the selected domain
  const loadImages = async (d = domain) => {
    if (!d) {
      setImages([]);
      return;
    }
    setLoadingImages(true);
    try {
      const r = await api.get(`/v1/images/list/${encodeURIComponent(d)}`);
      setImages(r.data);
      // Seed drafts from server values
      const seed = {};
      for (const img of r.data) {
        seed[img.id] = {
          alt_text_override: img.alt_text_override || "",
          is_decorative: !!img.is_decorative,
        };
      }
      setDrafts(seed);
    } catch {
      toast.error("Failed to load images");
    } finally {
      setLoadingImages(false);
    }
  };
  useEffect(() => {
    loadImages(domain);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [domain]);

  const dirtyIds = useMemo(() => {
    const ids = [];
    for (const img of images) {
      const d = drafts[img.id];
      if (!d) continue;
      const serverAlt = img.alt_text_override || "";
      const serverDeco = !!img.is_decorative;
      if (d.alt_text_override !== serverAlt || d.is_decorative !== serverDeco) {
        ids.push(img.id);
      }
    }
    return ids;
  }, [drafts, images]);

  const onSaveAll = async () => {
    if (dirtyIds.length === 0) return;
    setSaving(true);
    try {
      const updates = dirtyIds.map((id) => ({
        id,
        alt_text_override: drafts[id].alt_text_override || null,
        is_decorative: drafts[id].is_decorative,
      }));
      const r = await api.post("/v1/images/bulk-update", { updates });
      toast.success(`Saved ${r.data.updated} change${r.data.updated === 1 ? "" : "s"}`);
      await loadImages();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const setDraft = (id, key, value) => {
    setDrafts((prev) => {
      const next = { ...prev, [id]: { ...prev[id], [key]: value } };
      // Toggling decorative ON should clear any stale alt text so the
      // saved record is internally consistent (decorative → alt="").
      if (key === "is_decorative" && value === true) {
        next[id].alt_text_override = "";
      }
      return next;
    });
  };

  const totals = useMemo(() => {
    const naked = images.filter((i) => !i.alt_text_override && !i.is_decorative).length;
    const approved = images.filter((i) => i.alt_text_override).length;
    const decorative = images.filter((i) => i.is_decorative).length;
    return { naked, approved, decorative, total: images.length };
  }, [images]);

  return (
    <div data-testid="image-manager-page">
      <PageHeader
        eyebrow="Module 05 · Image Asset Manager"
        title="Runtime image vulnerability harvest"
        description="Every Silent Shield deployment streams the URLs of images missing alt text back here. Approve a custom alt string (or mark the image decorative) and Shield will stamp it into the live DOM on every subsequent page load."
        testid="image-manager-header"
      />

      <div className="max-w-[1600px] mx-auto px-4 md:px-8 py-6">
        {/* Top controls */}
        <div className="grid grid-cols-1 md:grid-cols-[420px_1fr_auto] gap-4 mb-6 items-end">
          <div>
            <Label className="label-xs mb-1">Client Domain</Label>
            <Select value={domain} onValueChange={(v) => setDomain(v)} disabled={loadingDomains || (deployments.length === 0 && auditDomains.length === 0)}>
              <SelectTrigger
                className="rounded-sm h-11 border-zinc-950 font-mono text-sm"
                data-testid="image-domain-select-trigger"
              >
                <SelectValue placeholder={loadingDomains ? "Loading…" : "Select a domain"} />
              </SelectTrigger>
              <SelectContent>
                {deployments.length > 0 && (
                  <div className="px-2 pt-1.5 pb-0.5 text-[9px] uppercase tracking-widest text-zinc-500 font-bold">
                    Deployed clients
                  </div>
                )}
                {deployments.map((d) => (
                  <SelectItem key={d.id} value={d.client_domain} data-testid={`image-domain-option-${d.client_domain}`}>
                    <span className="font-mono">{d.client_domain}</span>
                    {d.client_business_name && (
                      <span className="text-zinc-500 ml-2">· {d.client_business_name}</span>
                    )}
                  </SelectItem>
                ))}
                {auditDomains.length > 0 && (
                  <div className="px-2 pt-2 pb-0.5 text-[9px] uppercase tracking-widest text-zinc-500 font-bold border-t border-zinc-200 mt-1">
                    Audit-only · server crawled · no shield deployed
                  </div>
                )}
                {auditDomains.map((d) => (
                  <SelectItem
                    key={`audit-${d.client_domain}`}
                    value={d.client_domain}
                    data-testid={`image-audit-domain-option-${d.client_domain}`}
                  >
                    <span className="font-mono">{d.client_domain}</span>
                    <span className="text-zinc-500 ml-2 text-[10px]">
                      · {d.naked_count} naked / {d.image_count} total
                    </span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-4 gap-2 text-center">
            <Stat label="Total" value={totals.total} testid="img-stat-total" />
            <Stat label="Naked" value={totals.naked} accent="text-red-600" testid="img-stat-naked" />
            <Stat label="Approved" value={totals.approved} accent="text-emerald-600" testid="img-stat-approved" />
            <Stat label="Decorative" value={totals.decorative} accent="text-blue-700" testid="img-stat-decorative" />
          </div>
          <div className="flex gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => loadImages()}
              disabled={loadingImages || !domain}
              className="rounded-sm border-zinc-950 font-bold uppercase tracking-wider h-11"
              data-testid="image-refresh-button"
            >
              <RefreshCcw className={`w-4 h-4 mr-2 ${loadingImages ? "animate-spin" : ""}`} /> Refresh
            </Button>
            <Button
              type="button"
              onClick={onSaveAll}
              disabled={saving || dirtyIds.length === 0}
              className="rounded-sm bg-blue-700 hover:bg-blue-800 text-white font-bold uppercase tracking-wider h-11 disabled:opacity-40"
              data-testid="image-save-button"
            >
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
              Save Changes {dirtyIds.length > 0 && <span className="ml-1 font-mono">({dirtyIds.length})</span>}
            </Button>
          </div>
        </div>

        {/* Table */}
        <div className="border border-zinc-950" data-testid="image-table-wrapper">
          <div className="bg-zinc-950 text-white px-4 py-2 flex items-center justify-between">
            <div className="label-xs text-zinc-400 flex items-center gap-2">
              <ImageIcon className="w-3.5 h-3.5" /> Harvested images
              {domain && <span className="font-mono text-[10px] text-zinc-500 normal-case">— {domain}</span>}
            </div>
            <div className="font-mono text-xs">{images.length}</div>
          </div>

          {!domain && !loadingDomains && (
            <div className="px-6 py-12 text-center text-zinc-500 text-sm">
              <AlertCircle className="w-5 h-5 inline mr-2 text-zinc-400" />
              No active Silent Shield deployments yet. Create one in <strong>Script Deployment</strong> first.
            </div>
          )}

          {domain && loadingImages && (
            <div className="px-6 py-12 text-center text-zinc-500"><Loader2 className="w-5 h-5 inline animate-spin" /></div>
          )}

          {domain && !loadingImages && images.length === 0 && (
            <div className="px-6 py-12 text-center text-zinc-500 text-sm" data-testid="image-empty-state">
              <ImageOff className="w-5 h-5 inline mr-2 text-zinc-400" />
              No naked images reported yet. The Silent Shield script will pipe them here as visitors hit the site.
            </div>
          )}

          {domain && !loadingImages && images.length > 0 && (
            <div className="overflow-x-auto">
              <table className="w-full border-collapse text-sm">
                <thead>
                  <tr className="border-b-2 border-zinc-950">
                    <th className="text-left px-3 py-2 text-[10px] font-bold uppercase tracking-wider w-24">Preview</th>
                    <th className="text-left px-3 py-2 text-[10px] font-bold uppercase tracking-wider">File / URL</th>
                    <th className="text-left px-3 py-2 text-[10px] font-bold uppercase tracking-wider w-44">Discovered On</th>
                    <th className="text-left px-3 py-2 text-[10px] font-bold uppercase tracking-wider">Custom Alt Text</th>
                    <th className="text-center px-3 py-2 text-[10px] font-bold uppercase tracking-wider w-32">Decorative</th>
                    <th className="text-right px-3 py-2 text-[10px] font-bold uppercase tracking-wider w-24">Seen</th>
                  </tr>
                </thead>
                <tbody>
                  {images.map((img, i) => {
                    const draft = drafts[img.id] || { alt_text_override: "", is_decorative: false };
                    const isDirty = dirtyIds.includes(img.id);
                    return (
                      <tr
                        key={img.id}
                        className={`border-t border-zinc-200 ${isDirty ? "bg-amber-50" : ""}`}
                        data-testid={`image-row-${img.id}`}
                      >
                        <td className="px-3 py-2 align-middle">
                          <div className="w-16 h-16 border border-zinc-300 bg-zinc-100 grid place-items-center overflow-hidden">
                            <img
                              src={img.image_url}
                              alt=""
                              role="presentation"
                              loading="lazy"
                              referrerPolicy="no-referrer"
                              className="max-w-full max-h-full object-contain"
                              onError={(e) => {
                                e.currentTarget.style.display = "none";
                                e.currentTarget.parentElement.classList.add("relative");
                                const ph = document.createElement("span");
                                ph.className = "text-[9px] font-mono text-zinc-400 text-center px-1";
                                ph.textContent = "preview blocked";
                                e.currentTarget.parentElement.appendChild(ph);
                              }}
                              data-testid={`image-preview-${img.id}`}
                            />
                          </div>
                        </td>
                        <td className="px-3 py-2 align-middle">
                          <div className="font-mono text-xs font-bold text-zinc-950 truncate max-w-[260px]" title={img.image_url}>
                            {filenameOf(img.image_url)}
                          </div>
                          <a
                            href={img.image_url}
                            target="_blank"
                            rel="noreferrer"
                            className="font-mono text-[10px] text-blue-700 hover:underline truncate inline-block max-w-[260px]"
                            data-testid={`image-link-${img.id}`}
                          >
                            {img.image_url}
                          </a>
                        </td>
                        <td className="px-3 py-2 align-middle" data-testid={`image-discovered-on-${img.id}`}>
                          {(() => {
                            const pages = (img.discovered_on_pages && img.discovered_on_pages.length > 0
                              ? img.discovered_on_pages
                              : img.page_url
                              ? [img.page_url]
                              : []);
                            if (pages.length === 0) {
                              return <span className="text-zinc-400 font-mono text-[11px]">—</span>;
                            }
                            const primary = pages[0];
                            const extra = pages.length - 1;
                            return (
                              <div title={pages.join("\n")}>
                                <a
                                  href={primary}
                                  target="_blank"
                                  rel="noreferrer"
                                  className="inline-flex items-center gap-1 font-mono text-[11px] text-zinc-800 hover:text-blue-700 hover:underline max-w-[180px] truncate"
                                >
                                  <FolderTree className="w-3 h-3 flex-shrink-0 text-zinc-400" />
                                  {pagePathOf(primary)}
                                </a>
                                {extra > 0 && (
                                  <div className="font-mono text-[10px] text-zinc-500 mt-0.5">
                                    + {extra} more page{extra === 1 ? "" : "s"}
                                  </div>
                                )}
                              </div>
                            );
                          })()}
                        </td>
                        <td className="px-3 py-2 align-middle">
                          <Input
                            value={draft.alt_text_override}
                            onChange={(e) => setDraft(img.id, "alt_text_override", e.target.value)}
                            placeholder="Describe this image…"
                            disabled={draft.is_decorative}
                            className="rounded-sm text-sm h-9"
                            maxLength={500}
                            data-testid={`image-alt-input-${img.id}`}
                          />
                        </td>
                        <td className="px-3 py-2 align-middle text-center">
                          <Checkbox
                            checked={draft.is_decorative}
                            onCheckedChange={(v) => setDraft(img.id, "is_decorative", !!v)}
                            aria-label="Mark image as decorative"
                            className="rounded-none border-zinc-950 data-[state=checked]:bg-zinc-950 data-[state=checked]:text-white"
                            data-testid={`image-decorative-${img.id}`}
                          />
                        </td>
                        <td className="px-3 py-2 align-middle text-right font-mono text-xs">
                          <div className="font-bold">{img.seen_count}</div>
                          <div className="text-[10px] text-zinc-500">{(img.last_seen || "").slice(0, 10)}</div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Footer help text */}
        {domain && images.length > 0 && (
          <p className="text-[11px] text-zinc-500 leading-snug mt-4 max-w-3xl">
            <strong>How runtime injection works:</strong> when you click <em>Save Changes</em>, the approved alt
            strings are written into MongoDB and injected into the very next
            <code className="bg-zinc-100 px-1 mx-1 font-mono">shield.js</code> served to <span className="font-mono">{domain}</span>.
            Silent Shield reads the image map at runtime and stamps <code className="bg-zinc-100 px-1 font-mono">alt="…"</code>
            attributes onto matching <code className="bg-zinc-100 px-1 font-mono">&lt;img&gt;</code> elements as pages load.
          </p>
        )}
      </div>
    </div>
  );
}

function Stat({ label, value, accent, testid }) {
  return (
    <div className="border border-zinc-950 p-2 bg-white" data-testid={testid}>
      <div className="label-xs">{label}</div>
      <div className={`font-display text-2xl font-black tracking-tighter ${accent || "text-zinc-950"}`}>{value}</div>
    </div>
  );
}
